import {right} from "@popperjs/core";
import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {DLPApi, type DLPPolicyPattern} from "components/src/api/admin/generated";
import {ActionsDropdown} from "components/src/common/ActionsDropdown";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {DialogCloseCause} from "components/src/common/Enums";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {
    Bin as DeleteIcon,
    DotsGrid3x3 as GroupIcon,
    DotsGrid3x3 as AddGroupIcon,
    Plus as AddPatternIcon,
    ShieldEye as DLPIcon
} from "iconoir-react";
import {useCallback, useEffect, useState} from 'react'
import {Dropdown} from "react-bootstrap";
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import Tooltip from "react-bootstrap/Tooltip";
import {useNavigate} from "react-router-dom";
import {DLPColumn} from "./DLPColumn";
import {DLPGroupDialog} from "./DLPGroupDialog";
import {DLPPatternDialog} from "./DLPPatternDialog";
import {DLPTableHeader} from "./DLPTableHeader";

/*
 * REST api for accessing the back-end
 */
const dlpApi = new DLPApi(ApiConfig.config());

export const DLPPatterns = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * All patterns
     */
    const [patterns, setPatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * The total number of patterns
     */
    const [patternsCount, setPatternsCount] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected patterns
     */
    const [selectedPatterns, setSelectedPatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * The clicked pattern
     */
    const [clickedPattern, setClickedPattern] = useState<DLPPolicyPattern>();
    /**
     * Incremented whenever the data should be reloaded from the back-end. A counter is used instead of a boolean
     * because every increment is a new value, so a reload is never dropped and the load effects run exactly once
     * per reload request
     */
    const [reloadCounter, setReloadCounter] = useState<number>(0);
    /**
     * Forces the data to be reloaded from the back-end
     */
    const forceReload = useCallback(() => setReloadCounter(counter => counter + 1), []);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "add pattern" modal dialog will be shown
     */
    const [showAddPattern, setShowAddPattern] = useState<boolean>(false);
    /**
     * If true, the "edit pattern" modal dialog will be shown
     */
    const [showEditPattern, setShowEditPattern] = useState<boolean>(false);
    /**
     * If true, the "add group" modal dialog will be shown
     */
    const [showAddGroup, setShowAddGroup] = useState<boolean>(false);
    /**
     * If true, the "edit pattern" modal dialog will be shown
     */
    const [showEditGroup, setShowEditGroup] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() =>
    {
        logger.debug("Load pattern count")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        dlpApi.dLPControllerGetPolicyPatternCount()
            .then(response => {
                if (superseded) {
                    return;
                }

                setPatternsCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting pattern count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [navigate,
        reloadCounter,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load patterns")

        // reset the state when new data is loaded
        setSelectedPatterns([]);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        dlpApi.dLPControllerGetPolicyPatterns((currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(response => {
                if (superseded) {
                    return;
                }

                setPatterns(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting patterns", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [navigate,
        reloadCounter,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    const onChangeSelect = (checked : boolean, pattern : DLPPolicyPattern) =>
    {
        setSelectedPatterns(checked ?
            [...selectedPatterns, pattern] :
            selectedPatterns.filter(p => p.name !== pattern.name)
        );
    }

    const invertSelection = () => {
        setSelectedPatterns([...patterns].filter(p => !selectedPatterns.includes(p) && !p.inUse));
    }

    const deleteSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerDeletePolicyPatterns(selectedPatterns.map(p => p.name))
            .then(() => {
                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting patterns", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedPatterns([]);
            })
    }

    /**
     * Called when the AddPatternDialog is closed
     */
    const onAddPatternDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowAddPattern(false);

        if (closeCause === DialogCloseCause.Succes) {
            forceReload();
        }
    }

    /**
     * Called when the EditPatternDialog is closed
     */
    const onEditPatternDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowEditPattern(false);

        if (closeCause === DialogCloseCause.Succes) {
            forceReload();
        }
    }

    /**
     * Called when the AddGroupDialog is closed
     */
    const onAddGroupDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowAddGroup(false);

        if (closeCause === DialogCloseCause.Succes) {
            forceReload();
        }
    }

    /**
     * Called when the EditGroupDialog is closed
     */
    const onEditGroupDialogClose = () => {
        setShowEditGroup(false);
    }

    const editPattern = (pattern : DLPPolicyPattern) =>
    {
        setClickedPattern(pattern);
        setShowEditPattern(true);
    }

    const editGroup = (pattern : DLPPolicyPattern) =>
    {
        setClickedPattern(pattern);
        setShowEditGroup(true);
    }

    const onClickColumn = (pattern: DLPPolicyPattern) => {
        pattern.group ? editGroup(pattern) : editPattern(pattern);
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("dlp.are-you-sure-delete-patterns")}
                           confirmButtonTitle="Delete"
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showAddPattern && <DLPPatternDialog onClose={onAddPatternDialogClose} />}
            {showEditPattern && <DLPPatternDialog editName={clickedPattern?.name} onClose={onEditPatternDialogClose} />}
            {showAddGroup && <DLPGroupDialog onClose={onAddGroupDialogClose} />}
            {showEditGroup && <DLPGroupDialog editName={clickedPattern?.name} onClose={onEditGroupDialogClose} />}

            <div className="d-flex flex-column h-100">
                <PageHeader icon={<DLPIcon />} title={t("dlp.title")} onClickReload={forceReload}/>

                <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

                <div className="px-4">
                    <Row>
                        <div className="d-flex flex-row align-items-center mb-4">
                            <div className="d-flex flex-fill justify-content-end align-items-center">
                                <ActionsDropdown>
                                    <Dropdown.Item onClick={() => setShowAddPattern(true)}>
                                        <AddPatternIcon className="text-success"/> {t("dlp.add-pattern")}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setShowAddGroup(true)}>
                                        <AddGroupIcon className="text-success"/> {t("dlp.add-group")}
                                    </Dropdown.Item>
                                </ActionsDropdown>
                            </div>
                        </div>
                    </Row>
                </div>

                <PageContent className="flex-grow-1 min-h-0">
                    <Row className="h-100 w-100">
                        <Col className="h-100 overflow-auto">
                            <Table className="sticky sticky-x" bordered hover>
                                {selectedPatterns.length === 0 ?
                                    <DLPTableHeader>
                                        <InvertSelection invertSelectionEvent={invertSelection}/>
                                    </DLPTableHeader>
                                    :
                                    <thead className="sticky-top">
                                    <tr>
                                        <th colSpan={9}>
                                            <Navbar className="bg-body-tertiary ps-2">
                                                {/* Add icon to first column which will clear the selection when clicked */}
                                                <ClearSelection clearSelectionEvent={() => setSelectedPatterns([])} checked={true}/>

                                                <Navbar.Text className="ms-2">{selectedPatterns.length} selected</Navbar.Text>

                                                <div className="vr ms-4 me-2"></div>

                                                <Nav>
                                                    <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                        <DeleteIcon className="text-danger"/> {t("dlp.delete-selected")}
                                                    </Nav.Link>
                                                </Nav>
                                            </Navbar>
                                        </th>
                                    </tr>
                                    </thead>
                                }
                                <tbody>
                                {patterns?.map((p: DLPPolicyPattern) => (
                                    <tr key={p.name}>
                                        <DLPColumn className="td-w-min">
                                            <Form.Check
                                                type="checkbox"
                                                className="me-2"
                                                checked={selectedPatterns.find((spattern) =>  spattern.name === p.name) !== undefined}
                                                onChange={(e) => onChangeSelect(e.target.checked, p)}
                                                disabled={p.inUse}
                                                inline
                                            />
                                            {p.group &&
                                                <OverlayTrigger
                                                    placement={right}
                                                    overlay={
                                                        <Tooltip>
                                                            {t("dlp.group")}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <GroupIcon className="text-success"/>
                                                </OverlayTrigger>
                                            }
                                        </DLPColumn>

                                        <DLPColumn value={p.name} onClick={() => onClickColumn(p)}/>
                                        <DLPColumn value={p.description}  onClick={() => onClickColumn(p)}/>
                                        <DLPColumn value={p.regEx} onClick={() => onClickColumn(p)}/>
                                        <DLPColumn value={p.validator} onClick={() => onClickColumn(p)}/>
                                        <DLPColumn value={p.group ? "" : `${p.threshold}`} onClick={() => onClickColumn(p)}/>
                                        <DLPColumn value={p.action} onClick={() => onClickColumn(p)}/>
                                        <DLPColumn value={p.group ? "" : `${p.delayEvaluation}`} onClick={() => onClickColumn(p)}/>
                                        <DLPColumn value={p.matchFilter} onClick={() => onClickColumn(p)}/>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </PageContent>

                <PageFooter
                    left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>}
                    right={<Pager itemCount={patternsCount ?? 0}
                                  currentPage={currentPage}
                                  setCurrentPage={setCurrentPage}
                                  maxItemsPerPage={rowsPerPage}
                    />}
                />
            </div>
        </>
    );
}
